import { db } from './config';
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Servicio para gestión de pedidos usando Firebase
 */
const OrderService = {
  /**
   * Obtener todos los pedidos con filtros opcionales
   * @param {Object} filters - Filtros para los pedidos (fecha, estado, tipo, etc.)
   * @param {number} pageSize - Tamaño de página para paginación
   * @param {Object} lastVisible - Último documento visible para paginación
   */
  getOrders: async (filters = {}, pageSize = 20, lastVisible = null) => {
    try {
      const ordersRef = collection(db, 'orders');
      
      // Crear un array de restricciones para la consulta
      const constraints = [];
      
      // Aplicar filtros
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      
      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      
      if (filters.paymentStatus) {
        constraints.push(where('paymentStatus', '==', filters.paymentStatus));
      }
      
      // Filtrar por fecha
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
        constraints.push(where('date', '>=', startDate.toISOString().split('T')[0]));
      }
      
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        constraints.push(where('date', '<=', endDate.toISOString().split('T')[0]));
      }
      
      // Ordenar por fecha descendente (los más recientes primero)
      constraints.push(orderBy('date', 'desc'));
      constraints.push(orderBy('time', 'desc'));
      
      // Limitar resultados para paginación
      constraints.push(limit(pageSize));
      
      // Si hay un punto de inicio para paginación
      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }
      
      // Crear la consulta
      const q = query(ordersRef, ...constraints);
      
      // Ejecutar la consulta
      const querySnapshot = await getDocs(q);
      
      // Obtener el último documento visible para la siguiente página
      const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      // Mapear los resultados
      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Si hay un filtro de búsqueda por texto, lo aplicamos en memoria
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const filteredOrders = orders.filter(order => 
          order.id.toLowerCase().includes(searchLower) ||
          order.customer.name.toLowerCase().includes(searchLower) ||
          order.customer.phone.includes(filters.search) ||
          order.customer.email.toLowerCase().includes(searchLower)
        );
        
        return {
          orders: filteredOrders,
          lastVisible: lastVisibleDoc
        };
      }
      
      return {
        orders,
        lastVisible: lastVisibleDoc
      };
    } catch (error) {
      console.error('Error al obtener pedidos:', error);
      throw error;
    }
  },
  /**
   * Obtener pedidos del día actual
   */
  getTodayOrders: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      return await OrderService.getOrders({ startDate: today, endDate: today }, 100);
    } catch (error) {
      console.error('Error al obtener pedidos de hoy:', error);
      throw error;
    }
  },

  /**
   * Obtener un pedido específico por ID
   * @param {string} orderId - ID del pedido a obtener
   */
  getOrderById: async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        return {
          id: orderId,
          ...orderSnap.data()
        };
      } else {
        throw new Error('Pedido no encontrado');
      }
    } catch (error) {
      console.error(`Error al obtener pedido ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Crear un nuevo pedido
   * @param {Object} orderData - Datos del pedido a crear
   */
  createOrder: async (orderData) => {
    try {
      // Añadir campos automáticos
      const now = new Date();
      const orderToSave = {
        ...orderData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Si no se proporciona un estado, se establece como pendiente por defecto
        status: orderData.status || 'pending'
      };
      
      const docRef = await addDoc(collection(db, 'orders'), orderToSave);
      return {
        id: docRef.id,
        ...orderToSave,
        createdAt: now.toISOString() // Para la respuesta, ya que serverTimestamp() no se puede serializar
      };
    } catch (error) {
      console.error('Error al crear pedido:', error);
      throw error;
    }
  },

  /**
   * Actualizar el estado de un pedido
   * @param {string} orderId - ID del pedido a actualizar
   * @param {string} status - Nuevo estado del pedido
   */
  updateOrderStatus: async (orderId, status) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      await updateDoc(orderRef, { 
        status,
        updatedAt: serverTimestamp()
      });
      
      return {
        id: orderId,
        status,
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error al actualizar estado del pedido ${orderId}:`, error);
      throw error;
    }
  },
  /**
   * Actualizar estado de pago de un pedido
   * @param {string} orderId - ID del pedido a actualizar
   * @param {string} paymentStatus - Nuevo estado de pago
   * @param {string} paymentMethod - Método de pago (opcional)
   */
  updatePaymentStatus: async (orderId, paymentStatus, paymentMethod = null) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      const updateData = { 
        paymentStatus,
        updatedAt: serverTimestamp()
      };
      
      // Si se proporciona un método de pago, también lo actualizamos
      if (paymentMethod) {
        updateData.paymentMethod = paymentMethod;
      }
      
      await updateDoc(orderRef, updateData);
      
      return {
        id: orderId,
        ...updateData,
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error al actualizar estado de pago del pedido ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de pedidos
   * @param {string} startDate - Fecha inicial para las estadísticas
   * @param {string} endDate - Fecha final para las estadísticas
   */
  getOrderStats: async (startDate, endDate) => {
    try {
      // Obtener todos los pedidos en el rango de fechas
      const { orders } = await OrderService.getOrders({ 
        startDate, 
        endDate 
      }, 1000); // Limitamos a 1000 pedidos para las estadísticas
      
      // Calcular estadísticas
      const stats = {
        period: {
          start: startDate,
          end: endDate
        },
        totals: {
          orders: orders.length,
          completed: orders.filter(o => ['completed', 'delivered'].includes(o.status)).length,
          cancelled: orders.filter(o => o.status === 'cancelled').length,
          revenue: orders
            .filter(o => ['completed', 'delivered'].includes(o.status) && o.paymentStatus === 'paid')
            .reduce((sum, order) => sum + order.total, 0)
        },
        by_type: {
          local: orders.filter(o => o.type === 'local').length,
          takeaway: orders.filter(o => o.type === 'takeaway').length,
          delivery: orders.filter(o => o.type === 'delivery').length
        },
        by_status: {
          pending: orders.filter(o => o.status === 'pending').length,
          confirmed: orders.filter(o => o.status === 'confirmed').length,
          in_preparation: orders.filter(o => o.status === 'in_preparation').length,
          ready: orders.filter(o => o.status === 'ready').length,
          in_delivery: orders.filter(o => o.status === 'in_delivery').length,
          delivered: orders.filter(o => o.status === 'delivered').length,
          completed: orders.filter(o => o.status === 'completed').length,
          cancelled: orders.filter(o => o.status === 'cancelled').length
        },
        by_payment: {
          paid: orders.filter(o => o.paymentStatus === 'paid').length,
          pending: orders.filter(o => o.paymentStatus === 'pending').length
        }
      };
      
      return stats;
    } catch (error) {
      console.error('Error al obtener estadísticas de pedidos:', error);
      throw error;
    }
  },
  /**
   * Cancelar un pedido
   * @param {string} orderId - ID del pedido a cancelar
   * @param {string} reason - Motivo de la cancelación
   */
  cancelOrder: async (orderId, reason = '') => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      await updateDoc(orderRef, { 
        status: 'cancelled',
        cancellationReason: reason,
        updatedAt: serverTimestamp()
      });
      
      return {
        id: orderId,
        status: 'cancelled',
        cancellationReason: reason,
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error al cancelar pedido ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Obtener un resumen de pedidos para el dashboard
   */
  getDashboardSummary: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Obtener pedidos de hoy
      const { orders: todayOrders } = await OrderService.getOrders({ 
        startDate: today, 
        endDate: today 
      }, 1000);
      
      // Calcular estadísticas para el dashboard
      const summary = {
        today: {
          total: todayOrders.length,
          pending: todayOrders.filter(o => o.status === 'pending').length,
          in_progress: todayOrders.filter(o => 
            ['confirmed', 'in_preparation', 'ready', 'in_delivery'].includes(o.status)
          ).length,
          completed: todayOrders.filter(o => 
            ['completed', 'delivered'].includes(o.status)
          ).length,
          cancelled: todayOrders.filter(o => o.status === 'cancelled').length,
          revenue: todayOrders
            .filter(o => o.paymentStatus === 'paid')
            .reduce((sum, order) => sum + order.total, 0),
          by_type: {
            local: todayOrders.filter(o => o.type === 'local').length,
            takeaway: todayOrders.filter(o => o.type === 'takeaway').length,
            delivery: todayOrders.filter(o => o.type === 'delivery').length
          }
        },
        next_orders: todayOrders
          .filter(o => 
            ['pending', 'confirmed', 'in_preparation', 'ready'].includes(o.status)
          )
          .sort((a, b) => a.time.localeCompare(b.time))
          .slice(0, 5)
      };
      
      return summary;
    } catch (error) {
      console.error('Error al obtener resumen del dashboard:', error);
      throw error;
    }
  }
};

export default OrderService;